import 'swiper/css';
import 'swiper/css/a11y';
import React, { useRef, AllHTMLAttributes, useState } from 'react';
import cx from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './TabsModule.module.scss';

import { TabItem as TabItemType, TabsAssembly } from 'api/contentful/contentTypes.cloverdotcom';

import { Text } from '../Contentful/Text';
import { TabItem } from './TabItem';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';
import { ReferencedEntry } from 'contentful';
import { useTranslation } from 'react-i18next';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { ArrowButton, ArrowType } from 'components/ArrowButton/ArrowButton';

interface ITabsModuleProps extends AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<TabsAssembly>;
}

interface ISwiperClass {
  slidePrev: () => void;
  slideNext: () => void;
  slideTo: (index: number) => void;
  update: () => void;
  activeIndex: number;
  isBeginning: boolean;
  isEnd: boolean;
}

export const TabsModule: React.FC<ITabsModuleProps> = props => {
  const { entry, className, ...otherProps } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useTranslation();
  const { browser } = useBreakpoints();
  const isDesktop = browser.is.desktop && !browser.greaterThan.desktop;
  const swipperRef = useRef<{
    swiper: ISwiperClass & {
      hostEl?: HTMLElement;
      wrapperEl?: HTMLElement;
      slides?: HTMLElement[];
    };
  } | null>(null);

  if (!entry || !entry.fields) return null;
  const { items, backgroundColor, headingTagName = 'h2' } = entry.fields;

  if (!items || items.length < 2) {
    return null;
  }

  const swiperProps = {
    speed: 500,
    slidesPerView: 1,
    watchSlidesProgress: true,
    autoHeight: true,
  };

  const slideTo = (index: number) => {
    swipperRef.current?.swiper.slideTo(index);
    setActiveIndex(index);
  };

  const slideNext = () => {
    swipperRef.current?.swiper.slideNext();
    setActiveIndex(swipperRef.current?.swiper.activeIndex || 0);
  };

  const slidePrev = () => {
    swipperRef.current?.swiper.slidePrev();
    setActiveIndex(swipperRef.current?.swiper.activeIndex || 0);
  };

  const handleOnSlideChange = () => {
    setActiveIndex(swipperRef.current?.swiper.activeIndex || 0);
  };

  const arrowButtons = (
    <div className={cx(styles.tabNavigationArrows)}>
      <ArrowButton
        arrowDirection={ArrowType.PREV}
        onClick={slidePrev}
        aria-label={t('carousel.previous-item')}
        classNameOverride={cx(styles.tabNavigationArrow, {
          [styles.arrowDisabled]: swipperRef.current?.swiper.isBeginning,
        })}
      />
      <ArrowButton
        arrowDirection={ArrowType.NEXT}
        aria-label={t('carousel.next-item')}
        onClick={slideNext}
        classNameOverride={cx(styles.tabNavigationArrow, {
          [styles.arrowDisabled]: swipperRef.current?.swiper.isEnd,
        })}
      />
    </div>
  );

  return (
    <section className={cx(styles.tabsModuleWrapper, className)} style={{ backgroundColor }} {...otherProps}>
      <div className={styles.container}>
        <FadeToastUp>
          <>
            <Text tagName={headingTagName} field="title" className={styles.title} entry={entry} />
            <nav className={styles.tabsPanelContainer}>
              <ul className={styles.tabsPanel}>
                {items.map((item: ReferencedEntry<TabItemType>, index) => (
                  <li key={item.sys.id}>
                    <button
                      onClick={() => slideTo(index)}
                      className={cx(styles.tab, {
                        [styles.tabActive]: index === activeIndex,
                      })}
                    >
                      {item.fields?.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </>
        </FadeToastUp>
        <Swiper
          {...swiperProps}
          className={styles.slider}
          ref={swipperRef}
          onSlideChange={handleOnSlideChange}
        >
          {items.map((item: ReferencedEntry<TabItemType>) => (
            <SwiperSlide key={item.sys.id}>
              {({ isActive }: { isActive: boolean }) => (
                <div className={cx(!isActive && styles.disbaleTab)}>
                  <TabItem key={item.sys.id} item={item} />
                </div>
              )}
            </SwiperSlide>
          ))}
          {!isDesktop && arrowButtons}
        </Swiper>
      </div>
    </section>
  );
};

export default TabsModule;
